'use client'
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import GujaratiInput from '@/components/GujaratiInput';

export default function RegistrationPage() {
    const router = useRouter();
    const [language, setLanguage] = useState('english');
    const [role, setRole] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        fatherName: '',
        surname: '',
        nickname: '',
        spouseName: '',
        anniversaryDate: '',
        spouseProfession: '',
        standard: '10th',
        transactionId: '',
        qualification: '',
        profession: '',
        company: '',
        designation: '',
        residentialAddress: '',
        officeAddress: '',
        schoolMemories: '',
        messageAfter25: '',
        favoriteTeacher: '',
        businessDetails: ''
    });
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [modalVisible, setModalVisible] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalMessage, setModalMessage] = useState('');
    const [modalType, setModalType] = useState('info');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddChild = () => {
        setChildren([...children, { name: '', dateOfBirth: '' }]);
    };

    const handleChildChange = (index, field, value) => {
        const updatedChildren = [...children];
        updatedChildren[index][field] = value;
        setChildren(updatedChildren);
    };

    const handleRemoveChild = (index) => {
        setChildren(children.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        // Client-side required-fields validation
        const form = e.target;
        const entries = Object.fromEntries(new FormData(form).entries());
        const required = [
            { name: 'name', label: 'Name' },
            { name: 'fatherName', label: "Father's Name" },
            { name: 'surname', label: 'Surname' },
            { name: 'mobile', label: 'Mobile' },
            { name: 'whatsapp', label: 'WhatsApp' },
            { name: 'dob', label: 'Date of Birth' },
            { name: 'gender', label: 'Gender' },
            { name: 'secretKey', label: 'Secret Key' }
        ];

        const missing = required.filter(f => {
            const v = entries[f.name];
            return v === undefined || v === null || String(v).trim() === '';
        }).map(f => f.label);

        if (missing.length > 0) {
            setModalType('error');
            setModalTitle('Missing required fields');
            setModalMessage(`Please fill the following required fields: ${missing.join(', ')}`);
            setModalVisible(true);
            setLoading(false);
            return;
        }

        try {
            const formDataToSend = new FormData(form);

            // Add language and role
            formDataToSend.append('language', language);
            formDataToSend.append('role', role);

            // Add children data as JSON
            formDataToSend.append('children', JSON.stringify(children));

            const response = await fetch('/api/registration', {
                method: 'POST',
                body: formDataToSend,
            });

            const data = await response.json();

            if (response.ok) {
                setModalType('success');
                setModalTitle('Registration successful');
                setModalMessage('You have been registered successfully. Redirecting to login page...');
                setModalVisible(true);
                form.reset();
                setFormData({
                    name: '', fatherName: '', surname: '', nickname: '',
                    spouseName: '', anniversaryDate: '', spouseProfession: '',
                    standard: '', transactionId: '', qualification: '',
                    profession: '', company: '', designation: '',
                    residentialAddress: '', officeAddress: '',
                    schoolMemories: '', messageAfter25: '',
                    favoriteTeacher: '', businessDetails: ''
                });
                setTimeout(() => {
                    setModalVisible(false);
                    router.push('/login');
                }, 5000);
            } else {
                // Show server-provided error (including invalid secret key)
                setModalType('error');
                setModalTitle('Registration failed');
                setModalMessage(data.error || 'Registration failed. Please try again.');
                setModalVisible(true);
            }
        } catch (error) {
            console.error('Error:', error);
            setModalType('error');
            setModalTitle('Error');
            setModalMessage('An error occurred. Please try again.');
            setModalVisible(true);
        } finally {
            setLoading(false);
        }
    };

    const countries = [
        "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda",
        "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas",
        "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize",
        "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil",
        "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia",
        "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China",
        "Colombia", "Comoros", "Congo, Democratic Republic of the", "Congo, Republic of the",
        "Costa Rica", "Côte d'Ivoire", "Croatia", "Cuba", "Cyprus", "Czech Republic",
        "Denmark", "Djibouti", "Dominica", "Dominican Republic", "East Timor", "Ecuador",
        "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini",
        "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia",
        "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau",
        "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia",
        "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan",
        "Kazakhstan", "Kenya", "Kiribati", "Korea, North", "Korea, South", "Kosovo",
        "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia",
        "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi",
        "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania",
        "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia",
        "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru",
        "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria",
        "North Macedonia", "Norway", "Oman", "Palau", "Palestine",
        "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland",
        "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis",
        "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino",
        "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles",
        "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands",
        "Somalia", "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan",
        "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan",
        "Tanzania", "Thailand", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia",
        "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates",
        "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu",
        "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
    ];

    return (
        <div className="registra_fo_container">
            <div className="registra_fo_header">
                <h1 className="registra_fo_title">SSC Batch 2001 - Silver Jubilee Registration <br /> ( એસ.એસ.સી. બેચ 2001 – રજત જયંતિ સ્નેહ-મિલન નોંધણી ફોર્મ)</h1>
                <p className="registra_fo_subtitle">"સંબંધોનું રિચાર્જ - ૨૫ વર્ષની ઉજવણી!"</p>
            </div>
            <section className="registra_fo_intro">
                <h2 className="registra_fo_section_title">સ્વાગત સંદેશ</h2>
                <p className="registra_fo_text">
                    વહાલા મિત્રો,
                </p>
                <p className="registra_fo_text">
                    ધોરણ ૧૦ પછી વિતેલા ૨૫ વર્ષોની સ્મૃતિઓને ફરી જીવંત કરવા માટે આયોજિત "સ્નેહ મિલન અને ગુરુવંદના"
                    કાર્યક્રમમાં આપ સૌનું હાર્દિક સ્વાગત છે.
                </p>
                <p className="registra_fo_text">
                    આ કાર્યક્રમના સુચારુ આયોજન માટે, જમણવારની ચોક્કસ સંખ્યા જાણવા અને આપના માટે બીજી વ્યવસ્થાઓ તૈયાર કરવા માટે આ ફોર્મ ભરવું અનિવાર્ય છે.
                </p>
                <p className="registra_fo_text">
                    તમે રૂબરૂ (Physically) આવવાના હોવ કે ઓનલાઈન (Virtually) જોડાવાના હોવ, કૃપા કરીને નીચે મુજબની વિગતો ભરી આપશો.
                </p>
            </section>

            <div className="registra_fo_main">
                <form className="registra_fo_form" onSubmit={handleSubmit}>
                    <h2 className="registra_fo_form_title">Registration Form / નોંધણી ફોર્મ</h2>
                    {/* Language Selection */}
                    <div className="language_selector" style={{ marginBottom: '2rem', textAlign: 'center' }}>
                        <label style={{ marginRight: '1rem', fontSize: '.8rem', fontWeight: 'bold' }}>
                            Choose writing Language / લખવાની ભાષા પસંદ કરો:
                        </label>
                        <button
                            type="button"
                            onClick={() => setLanguage('english')}
                            style={{
                                padding: '0.5rem 1.5rem',
                                margin: '0.5rem',
                                backgroundColor: language === 'english' ? '#4CAF50' : '#ddd',
                                color: language === 'english' ? 'white' : 'black',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '1rem'
                            }}
                        >
                            English
                        </button>
                        <button
                            type="button"
                            onClick={() => setLanguage('gujarati')}
                            style={{
                                padding: '0.5rem 1.5rem',
                                margin: '0.5rem',
                                backgroundColor: language === 'gujarati' ? '#4CAF50' : '#ddd',
                                color: language === 'gujarati' ? 'white' : 'black',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '1rem'
                            }}
                        >
                            ગુજરાતી
                        </button>
                    </div>
                    <div className="registra_fo_form_group" style={{ textAlign: 'center', marginBottom: '1rem' }}>
                        <label className="registra_fo_label" htmlFor="secretKey">
                            Secret Key / સિક્રેટ કી <span className="registra_fo_required">(ફરજિયાત)</span>
                        </label>
                        <input
                            type="password"
                            id="secretKey"
                            name="secretKey"
                            className="registra_fo_input"
                            required
                            placeholder="Enter secret key"
                            style={{ maxWidth: '320px', margin: '0.5rem auto', display: 'block' }}
                        />
                    </div>
                    {message && (
                        <div style={{
                            padding: '1rem',
                            marginBottom: '1rem',
                            backgroundColor: message.includes('successful') ? '#d4edda' : '#f8d7da',
                            color: message.includes('successful') ? '#155724' : '#721c24',
                            borderRadius: '5px',
                            textAlign: 'center'
                        }}>
                            {message}
                        </div>
                    )}

                    {/* Role Selection */}
                    {/* <div className="registra_fo_form_group">
                        <label className="registra_fo_label">
                            તમે શિક્ષક હતા કે વિદ્યાર્થી હતા? / Were you a teacher or a student?*
                            <span className="registra_fo_required">(ફરજિયાત)</span>
                        </label>
                        <div className="registra_fo_radio_group">
                            <label className="registra_fo_radio_label">
                                <input
                                    type="radio"
                                    name="role"
                                    value="teacher"
                                    className="registra_fo_radio"
                                    // required
                                    onChange={(e) => setRole(e.target.value)}
                                /> &nbsp; શિક્ષક / Teacher
                            </label>
                            <label className="registra_fo_radio_label">
                                <input
                                    type="radio"
                                    name="role"
                                    value="student"
                                    className="registra_fo_radio"
                                    required
                                    onChange={(e) => setRole(e.target.value)}
                                /> &nbsp; વિદ્યાર્થી / Student
                            </label>
                        </div>
                    </div> */}

                    {/* Transaction ID - Required before form submission */}
                    {/* <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="transactionId">
                            Transaction ID
                        </label>
                        <GujaratiInput
                            id="transactionId"
                            name="transactionId"
                            value={formData.transactionId}
                            onChange={handleInputChange}
                            language={language}
                            className="registra_fo_input"
                            placeholder="તમારી પેમેન્ટનો Transaction ID દાખલ કરો"
                        />
                    </div> */}

                    {/* Photo Upload */}
                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="photo">
                            Profile Photo / પ્રોફાઇલ ફોટો:
                        </label>
                        <input
                            type="file"
                            id="photo"
                            name="photo"
                            className="registra_fo_file_input"
                            accept="image/*"
                        />
                    </div>

                    {/* Personal Information */}
                    <h3 className="registra_fo_section_subtitle">Profile / પ્રોફાઇલ:</h3>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="name">
                            Name* / નામ*  <span className="registra_fo_required">(ફરજિયાત)</span>
                        </label>
                        <GujaratiInput
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            language={language}
                            className="registra_fo_input"
                            required
                            placeholder="તમારું પૂરું નામ"
                        />
                    </div>

                    <div className="registra_fo_form_row">
                        <div className="registra_fo_form_group registra_fo_half">
                            <label className="registra_fo_label" htmlFor="fatherName">
                                Father’s Name*  <span className="registra_fo_required">(ફરજિયાત)</span>
                            </label>
                            <GujaratiInput
                                id="fatherName"
                                name="fatherName"
                                value={formData.fatherName}
                                onChange={handleInputChange}
                                language={language}
                                className="registra_fo_input"
                                required
                                placeholder="પિતાનું નામ"
                            />
                        </div>

                        <div className="registra_fo_form_group registra_fo_half">
                            <label className="registra_fo_label" htmlFor="surname">
                                Surname* / અટક*  <span className="registra_fo_required">(ફરજિયાત)</span>
                            </label>
                            <GujaratiInput
                                id="surname"
                                name="surname"
                                value={formData.surname}
                                onChange={handleInputChange}
                                language={language}
                                className="registra_fo_input"
                                required
                                placeholder="અટક"
                            />
                        </div>
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="nickname">
                            School-time Nickname (if any) / સ્કૂલ દરમિયાનનું ઉપનામ / નિકનેમ (જો હોય તો) :
                        </label>
                        <GujaratiInput
                            id="nickname"
                            name="nickname"
                            value={formData.nickname}
                            onChange={handleInputChange}
                            language={language}
                            className="registra_fo_input"
                            placeholder="તમારું ઉપનામ / Your Nickname"
                        />
                    </div>

                    <div className="registra_fo_form_row">
                        <div className="registra_fo_form_group registra_fo_half">
                            <label className="registra_fo_label" htmlFor="standard">
                                Standard / ધોરણ
                            </label>
                            <input
                                id="standard"
                                type='text'
                                name="standard"
                                value={formData.standard}
                                language={language}
                                className="registra_fo_input"
                                placeholder="ધોરણ 10th"
                                readOnly={true}
                            />
                        </div>

                        <div className="registra_fo_form_group registra_fo_half">
                            <label className="registra_fo_label">વર્ગ * / Class <span className="registra_fo_required">(ફરજિયાત)</span></label>
                            <div className="registra_fo_radio_group">
                                <label className="registra_fo_radio_label">
                                    <input type="radio" name="division" value="A" className="registra_fo_radio" /> &nbsp; અ
                                </label>
                                <label className="registra_fo_radio_label">
                                    <input type="radio" name="division" value="B" className="registra_fo_radio" /> &nbsp; બ
                                </label>
                                <label className="registra_fo_radio_label">
                                    <input type="radio" name="division" value="C" className="registra_fo_radio" /> &nbsp; ક
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="registra_fo_form_row">
                        <div className="registra_fo_form_group registra_fo_half">
                            <label className="registra_fo_label" htmlFor="dob">
                                Date of Birth* / જન્મ તારીખ*   <span className="registra_fo_required">(ફરજિયાત)</span>
                            </label>
                            <input
                                type="date"
                                id="dob"
                                name="dob"
                                onClick={(e) => e.target.showPicker()}
                                className="registra_fo_input"
                                required
                            />
                        </div>

                        <div className="registra_fo_form_group registra_fo_half">
                            <label className="registra_fo_label" htmlFor="bloodGroup">
                                Blood Group / રક્ત જૂથ :
                            </label>
                            <select id="bloodGroup" name="bloodGroup" className="registra_fo_select">
                                <option value="">પસંદ કરો</option>
                                <option value="A+">A+</option>
                                <option value="A-">A-</option>
                                <option value="B+">B+</option>
                                <option value="B-">B-</option>
                                <option value="O+">O+</option>
                                <option value="O-">O-</option>
                                <option value="AB+">AB+</option>
                                <option value="AB-">AB-</option>
                            </select>
                        </div>
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label">Gender / લિંગ <span className="registra_fo_required">(ફરજિયાત)</span></label>
                        <div className="registra_fo_radio_group">
                            <label className="registra_fo_radio_label">
                                <input type="radio" name="gender" value="Male" className="registra_fo_radio" required /> &nbsp; Male / પુરુષ
                            </label>
                            <label className="registra_fo_radio_label">
                                <input type="radio" name="gender" value="Female" className="registra_fo_radio" required /> &nbsp; Female / સ્ત્રી
                            </label>
                        </div>
                    </div>

                    {/* Professional Information */}
                    <h3 className="registra_fo_section_subtitle">Professional Information / વ્યવસાયિક માહિતી  </h3>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="qualification">
                            લાયકાત / અભ્યાસ (Qualification / Education)
                        </label>
                        <GujaratiInput
                            id="qualification"
                            name="qualification"
                            value={formData.qualification}
                            onChange={handleInputChange}
                            language={language}
                            className="registra_fo_input"
                            placeholder="તમારી શૈક્ષણિક લાયકાત"
                        />
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="profession">
                            વ્યવસાય / પ્રોફેશન * (Profession / Occupation) <span className="registra_fo_required">(ફરજિયાત)</span>
                        </label>
                        <GujaratiInput
                            id="profession"
                            name="profession"
                            value={formData.profession}
                            onChange={handleInputChange}
                            language={language}
                            className="registra_fo_input"
                            required
                            placeholder="તમારો વ્યવસાય"
                        />
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="company">
                            કંપની / સંસ્થા નામ (Company / Organization Name)
                        </label>
                        <GujaratiInput
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            language={language}
                            className="registra_fo_input"
                            placeholder="કંપની/સંસ્થાનું નામ"
                        />
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="designation">
                            હોદ્દો / જવાબદારી (Designation / Responsibility)
                        </label>
                        <GujaratiInput
                            id="designation"
                            name="designation"
                            value={formData.designation}
                            onChange={handleInputChange}
                            language={language}
                            className="registra_fo_input"
                            placeholder="તમારો હોદ્દો"
                        />
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="experience">
                            કુલ કાર્ય અનુભવ (વર્ષોમાં) / Total Work Experience (in years)
                        </label>
                        <input
                            type="number"
                            id="experience"
                            name="experience"
                            className="registra_fo_input"
                            min="0"
                            max="50"
                            placeholder="વર્ષો"
                        />
                    </div>

                    {/* Contact Information */}
                    <h3 className="registra_fo_section_subtitle">Contact Information / સંપર્ક માહિતી</h3>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="mobile">
                            મોબાઇલ નંબર * / Mobile Number*<span className="registra_fo_required">(ફરજિયાત)</span>
                        </label>
                        <input
                            type="tel"
                            id="mobile"
                            name="mobile"
                            className="registra_fo_input"
                            required
                            placeholder="મોબાઈલ નંબર"
                        />
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="whatsapp">
                            વોટ્સએપ નંબર * / WhatsApp Number* <span className="registra_fo_required">(ફરજિયાત)</span>
                        </label>
                        <input
                            type="tel"
                            id="whatsapp"
                            name="whatsapp"
                            className="registra_fo_input"
                            required
                            placeholder="વોટ્સએપ નંબર"
                        />
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="email">
                            ઇમેઇલ સરનામું / Email Address
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            className="registra_fo_input"
                            placeholder="ઇમેઇલ સરનામું"
                        />
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="residentialAddress">
                            રહેઠાણનું સરનામું * / Residential Address*  <span className="registra_fo_required">(ફરજિયાત)</span>
                        </label>
                        <GujaratiInput
                            id="residentialAddress"
                            name="residentialAddress"
                            value={formData.residentialAddress}
                            onChange={handleInputChange}
                            language={language}
                            className="registra_fo_input"
                            required
                            placeholder="શહેર, રાજ્ય"
                        />
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="country">
                            દેશ * / Country *
                            <span className="registra_fo_required">(ફરજિયાત)</span>
                        </label>

                        <select
                            name="country"
                            className="registra_fo_input"
                            defaultValue="India"
                            required
                        >
                            <option value="">Select Country</option>
                            {countries.map((country) => (
                                <option key={country} value={country}>
                                    {country}
                                </option>
                            ))}
                        </select>

                    </div>


                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="officeAddress">
                            ઓફિસનું સરનામું (શહેર) / Office Address (City)
                        </label>
                        <GujaratiInput
                            id="officeAddress"
                            name="officeAddress"
                            value={formData.officeAddress}
                            onChange={handleInputChange}
                            language={language}
                            className="registra_fo_input"
                            placeholder="ઓફિસ સરનામું"
                        />
                    </div>

                    {/* Social Media */}
                    {/* Family Information */}
                    <h3 className="registra_fo_section_subtitle">Family Information / પરિવારની માહિતી</h3>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="spouseName">
                            Spouse Name / life partner નું નામ
                        </label>
                        <GujaratiInput
                            id="spouseName"
                            name="spouseName"
                            value={formData.spouseName}
                            onChange={handleInputChange}
                            language={language}
                            className="registra_fo_input"
                            placeholder="પત્ની/પતિ નો નામ (જો લાગુ પડે)"
                        />
                    </div>

                    <div className="registra_fo_form_row">
                        <div className="registra_fo_form_group registra_fo_half">
                            <label className="registra_fo_label" htmlFor="anniversaryDate">
                                Anniversary Date / લગ્નની તારીખ
                            </label>
                            <input
                                type="date"
                                id="anniversaryDate"
                                name="anniversaryDate"
                                className="registra_fo_input"
                            />
                        </div>

                        <div className="registra_fo_form_group registra_fo_half">
                            <label className="registra_fo_label" htmlFor="spouseProfession">
                                Spouse Profession / જીવનસાથી વ્યવસાય
                            </label>
                            <GujaratiInput
                                id="spouseProfession"
                                name="spouseProfession"
                                value={formData.spouseProfession}
                                onChange={handleInputChange}
                                language={language}
                                className="registra_fo_input"
                                placeholder="વ્યવસાય"
                            />
                        </div>
                    </div>

                    {/* Children Information */}
                    <h3 className="registra_fo_section_subtitle">Children Information / બાળકોની માહિતી (વૈકલ્પિક)</h3>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label">
                            કેટલાં બાળકો છે? / How many children do you have?
                        </label>
                        <div style={{ marginTop: '0.5rem' }}>
                            <button
                                type="button"
                                onClick={handleAddChild}
                                style={{
                                    padding: '0.6rem 1.5rem',
                                    backgroundColor: '#4CAF50',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    transition: 'background-color 0.3s'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#45a049'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#4CAF50'}
                            >
                                + Add Child / બાળક ઉમેરો
                            </button>
                        </div>
                    </div>

                    {children.length > 0 && (
                        <div style={{
                            backgroundColor: '#f5f5f5',
                            padding: '1rem',
                            borderRadius: '8px',
                            marginTop: '1rem',
                            border: '1px solid #ddd'
                        }}>
                            {children.map((child, index) => (
                                <div
                                    key={index}
                                    style={{
                                        marginBottom: index < children.length - 1 ? '1.5rem' : '0',
                                        paddingBottom: index < children.length - 1 ? '1.5rem' : '0',
                                        borderBottom: index < children.length - 1 ? '1px solid #ddd' : 'none'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                        <h4 style={{ margin: '0', fontSize: '1.1rem', fontWeight: 'bold' }}>
                                            Child {index + 1}
                                        </h4>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveChild(index)}
                                            style={{
                                                padding: '0.4rem 0.8rem',
                                                backgroundColor: '#f44336',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>

                                    <div className="registra_fo_form_row" style={{ marginBottom: '1rem' }}>
                                        <div className="registra_fo_form_group registra_fo_half">
                                            <label className="registra_fo_label">
                                                Child Name / બાળકનું નામ
                                            </label>
                                            <input
                                                type="text"
                                                value={child.name}
                                                onChange={(e) => handleChildChange(index, 'name', e.target.value)}
                                                className="registra_fo_input"
                                                placeholder="બાળકનું નામ"
                                            />
                                        </div>

                                        <div className="registra_fo_form_group registra_fo_half">
                                            <label className="registra_fo_label">
                                                Date of Birth / જન્મ તારીખ
                                            </label>
                                            <input
                                                type="date"
                                                value={child.dateOfBirth}
                                                onChange={(e) => handleChildChange(index, 'dateOfBirth', e.target.value)}
                                                className="registra_fo_input"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <h3 className="registra_fo_section_subtitle">Social Media (Optional) / સોશિયલ મીડિયા (વૈકલ્પિક)</h3>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="instagram">
                            ઇન્સ્ટાગ્રામ પ્રોફાઇલ લિંક / Instagram Profile Link
                        </label>
                        <input
                            type="text"
                            id="instagram"
                            name="instagram"
                            className="registra_fo_input"
                            placeholder="Instagram પ્રોફાઇલ લિંક"
                        />
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="facebook">
                            ફેસબુક પ્રોફાઇલ લિંક / Facebook Profile Link
                        </label>
                        <input
                            type="text"
                            id="facebook"
                            name="facebook"
                            className="registra_fo_input"
                            placeholder="Facebook પ્રોફાઇલ લિંક"
                        />
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="linkedin">
                            લિન્કડઇન પ્રોફાઇલ લિંક / LinkedIn Profile Link (Recommended for professional networking)
                        </label>
                        <input
                            type="text"
                            id="linkedin"
                            name="linkedin"
                            className="registra_fo_input"
                            placeholder="LinkedIn પ્રોફાઇલ લિંક"
                        />
                    </div>

                    {/* Memory Section */}
                    <h3 className="registra_fo_section_subtitle">Memory Section / સ્મૃતિ વિભાગ</h3>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="schoolMemories">
                            સ્કૂલની યાદ (૪–૫ લાઈનો) / School Memories (4–5 lines) <br />
                            <span>(Any special moment, friendship, incident, or classroom memory)</span>
                        </label>
                        <GujaratiInput
                            id="schoolMemories"
                            name="schoolMemories"
                            value={formData.schoolMemories}
                            onChange={handleInputChange}
                            language={language}
                            className="registra_fo_textarea"
                            as="textarea"
                            rows="4"
                            placeholder="કોઈ ખાસ ક્ષણ, મિત્રતા, ઘટના અથવા વર્ગખંડની સ્મૃતિ"
                        />
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="messageAfter25">
                            ૨૫ વર્ષ પછીનો સંદેશ / Message After 25 Years <br />
                            <span>(Your thoughts about this reunion, friendship, or life)</span>
                        </label>
                        <GujaratiInput
                            id="messageAfter25"
                            name="messageAfter25"
                            value={formData.messageAfter25}
                            onChange={handleInputChange}
                            language={language}
                            className="registra_fo_textarea"
                            as="textarea"
                            rows="4"
                            placeholder="આ પુનર્મિલન, મિત્રતા અથવા જીવન અંગે તમારો સંદેશ"
                        />
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="favoriteTeacher">
                            મારો મનપસંદ ગુરુ / My Favourite Teacher<br />
                            <span>(Name with 1–2 lines describing why)</span>

                        </label>
                        <GujaratiInput
                            id="favoriteTeacher"
                            name="favoriteTeacher"
                            value={formData.favoriteTeacher}
                            onChange={handleInputChange}
                            language={language}
                            className="registra_fo_textarea"
                            as="textarea"
                            rows="3"
                            placeholder="નામ સાથે ૧–૨ લાઈનમાં કારણ લખશો તો ઉત્તમ"
                        />
                    </div>

                    {/* Business & Networking */}
                    <h3 className="registra_fo_section_subtitle">Business & Networking / વ્યવસાય અને જોડાણ</h3>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label">
                            બિઝનેસ / પ્રોફેશનલ જોડાણમાં રસ છે ? <br /> (Interested in Business / Professional Networking?)</label>
                        <div className="registra_fo_radio_group">
                            <label className="registra_fo_radio_label">
                                <input type="radio" name="networkingInterest" value="yes" className="registra_fo_radio" /> &nbsp; હા (yes)
                            </label>
                            <label className="registra_fo_radio_label">
                                <input type="radio" name="networkingInterest" value="no" className="registra_fo_radio" /> &nbsp; ના (no)
                            </label>
                        </div>
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label" htmlFor="businessDetails">
                            (હા હોય તો તમારી સેવાઓ / પ્રોડક્ટનો ટૂંકમાં ઉલ્લેખ કરો) <br /> (If yes, briefly mention your services / products)
                        </label>
                        <GujaratiInput
                            id="businessDetails"
                            name="businessDetails"
                            value={formData.businessDetails}
                            onChange={handleInputChange}
                            language={language}
                            className="registra_fo_textarea"
                            as="textarea"
                            rows="3"
                            placeholder="તમારી સેવાઓ / પ્રોડક્ટ વિશે"
                        />
                    </div>

                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label">
                            ભવિષ્યમાં ગ્રુપ પ્રવૃત્તિઓમાં જોડાવાની ઇચ્છા: <br /> (Interest in future group activities):
                        </label>
                        <div className="registra_fo_checkbox_group">
                            <label className="registra_fo_checkbox_label">
                                <input type="checkbox" name="groupActivities" value="social" className="registra_fo_checkbox" />&nbsp; સામાજિક (Social)
                            </label>
                            <label className="registra_fo_checkbox_label">
                                <input type="checkbox" name="groupActivities" value="business" className="registra_fo_checkbox" />&nbsp; વ્યવસાયિક (Business)
                            </label>
                            <label className="registra_fo_checkbox_label">
                                <input type="checkbox" name="groupActivities" value="cultural" className="registra_fo_checkbox" />&nbsp; સાંસ્કૃતિક (Cultural)
                            </label>
                            <label className="registra_fo_checkbox_label">
                                <input type="checkbox" name="groupActivities" value="all" className="registra_fo_checkbox" />&nbsp; બધામાં (All)
                            </label>
                        </div>
                    </div>

                    {/* Attendance */}
                    <div className="registra_fo_form_group">
                        <label className="registra_fo_label">
                            શું તમે ૦૩-મે-૨૦૨૬ ના રોજ સ્નેહ મિલનમાં હાજરી આપશો? <br /> (Will you attend the reunion on 03-May-2026?)  <br />
                            <span className="registra_fo_note">(અનિવાર્ય સંજોગોમાં તારીખ બદલાઈ શકે છે)</span>
                        </label>
                        <div className="registra_fo_radio_group">
                            <label className="registra_fo_radio_label">
                                <input type="radio" name="attendance" value="yes" className="registra_fo_radio" required />&nbsp; હા (yes)
                            </label>
                            <label className="registra_fo_radio_label">
                                <input type="radio" name="attendance" value="no" className="registra_fo_radio" required />&nbsp; ના (no)
                            </label>
                        </div>
                    </div>

                    {/* <section className="registra_fo_payment_info">
                        <h2 className="registra_fo_section_title">રજીસ્ટ્રેશન અને પેમેન્ટ વિગત</h2>
                        <div className="registra_fo_payment_box">
                            <p className="registra_fo_text">
                                <strong>નોંધ:</strong> આ ફોર્મ ભરતા પહેલા કૃપા કરીને નક્કી કરેલ ટોકન રકમ નીચેની બેંક વિગતો પર
                                જમા કરાવવા વિનંતી છે. (ફોર્મમાં Transaction ID નાખવો જરૂરી છે).
                                રજીસ્ટ્રેશન કે દાન ની રકમ કેશ માં લેવામાં આવશે નહિ.
                            </p>

                            <div className="registra_fo_fee_details">
                                <div className="registra_fo_fee_item">
                                    <span className="registra_fo_fee_label">રજીસ્ટ્રેશન ફી (Token):</span>
                                    <span className="registra_fo_fee_amount">₹ 500 (મરજીયાત)</span>
                                </div>
                                <div className="registra_fo_fee_item">
                                    <span className="registra_fo_fee_label">સ્વૈચ્છિક ડોનેશન:</span>
                                    <span className="registra_fo_fee_amount">આપની યથાશક્તિ મુજબ (આપણા પ્રસંગને વધુ ભવ્ય બનાવવા). બંને રકમ એક સાથે પણ જમા કરાવી શકાશે.</span>
                                </div>
                            </div>

                            <div className="registra_fo_bank_details">
                                <h3 className="registra_fo_bank_title">🏦 Bank Details for Payment:</h3>
                                <ul className="registra_fo_bank_list">
                                    <li><strong>Name:</strong> Manjulaben Satishbhai Prajapati (મંજુલાબેન સતીષભાઈ પ્રજાપતિ)</li>
                                    <li><strong>Bank Name:</strong> IndusInd Bank, Rakhial Branch, Ahmedabad</li>
                                    <li><strong>Account No:</strong> 150000161217</li>
                                    <li><strong>IFSC Code:</strong> INDB0001596</li>
                                    <li><strong>UPI ID (GPay/PhonePe):</strong> 9978888750@ibl</li>
                                    <li><strong>•	QR Code:</strong>Whatsapp ગ્રુપમાં શેર કરેલ છે.</li>
                                </ul>
                                <div className="registra_fo_note">
                                    <strong>નોંધ:</strong>
                                    <ul>
                                        <li>1. પેમેન્ટ કર્યા બાદ તેનો Transaction ID અથવા સ્ક્રીનશોટ સાચવી રાખવો.</li>
                                        <li>2. શક્ય હોય ત્યાં સુધી કૃપા કરીને વિગતો અંગ્રેજીમાં ભરવાનો પ્રયાસ કરશો.</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section> */}

                    {/* Privacy Note */}
                    <div className="registra_fo_privacy_note">
                        <p>
                            <strong>નોંધ:</strong> આ ફોર્મમાં આપેલ માહિતીનો ઉપયોગ માત્ર ૨૫ વર્ષીય પુનર્મિલન,
                            સ્મૃતિ સંગ્રહ, વેબસાઇટ અને મિત્ર જોડાણ માટે જ કરવામાં આવશે.
                        </p>
                    </div>

                    {/* Submit Button */}
                    <div className="registra_fo_form_group">
                        <button type="submit" className="registra_fo_submit_btn" disabled={loading}>
                            {loading ? 'Submitting...' : 'નોંધણી પૂર્ણ કરો'}
                        </button>
                    </div>
                </form>

                {/* Modal popup */}
                {modalVisible && (
                    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                        <div style={{ background: 'white', borderRadius: 8, maxWidth: '92%', width: 420, padding: '1rem 1.25rem', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <h3 style={{ margin: 0, fontSize: '1.05rem', color: modalType === 'success' ? '#155724' : (modalType === 'error' ? '#721c24' : '#0c5460') }}>{modalTitle}</h3>
                                <button onClick={() => setModalVisible(false)} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
                            </div>
                            <div style={{ marginBottom: 12, color: '#333', whiteSpace: 'pre-wrap' }}>{modalMessage}</div>
                            <div style={{ textAlign: 'right' }}>
                                <button onClick={() => {
                                    setModalVisible(false);
                                }} style={{ padding: '0.5rem 0.9rem', borderRadius: 6, border: 'none', background: modalType === 'success' ? '#28a745' : '#007bff', color: 'white', cursor: 'pointer' }}>OK</button>
                            </div>
                        </div>
                    </div>
                )}






            </div>

            <div className="registra_fo_footer">
                <p>&copy; આયોજન સમિતિ (SSC બેચ ૨૦૦૧)</p>
                <p className="registra_fo_footer_quote">ચાલો, ફરી એકવાર સાથે મળીને જૂની યાદો તાજી કરીએ!</p>
            </div>
        </div>
    );
}